import React from 'react'

import { $createLinkNode, $isAutoLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import { $findMatchingParent, mergeRegister } from '@lexical/utils'
import {
  $getSelection,
  $isRangeSelection,
  BaseSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  KEY_ESCAPE_COMMAND,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical'
import { Dispatch, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { getSelectedNode, sanitizeUrl, setFloatingElemPositionForLinkEditor } from './utils'
import { RichTextFormatting } from './hooks'
import { Form } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../../../../../../../components/Button/Button'

interface FloatingLinkEditorProps {
  editor: LexicalEditor
  isLink: boolean
  setIsLink: Dispatch<boolean>
  anchorElem: HTMLElement
  isLinkEditMode: boolean
  setIsLinkEditMode: Dispatch<boolean>
}

const FloatingLinkEditor: React.FC<FloatingLinkEditorProps> = ({ editor, isLink, setIsLink, anchorElem, isLinkEditMode, setIsLinkEditMode }) => {
  const editorRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [linkUrl, setLinkUrl] = useState('')
  const [editedLinkUrl, setEditedLinkUrl] = useState('https://')
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(null)

  const $updateLinkEditor = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection)
      const linkParent = $findMatchingParent(node, $isLinkNode)

      if (linkParent) {
        setLinkUrl(linkParent.getURL())
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL())
      } else {
        setLinkUrl('')
      }
      if (isLinkEditMode) {
        setEditedLinkUrl(linkUrl)
      }
    }
    const editorElem = editorRef.current
    const nativeSelection = window.getSelection()
    const activeElement = document.activeElement

    if (editorElem === null) {
      return
    }

    const rootElement = editor.getRootElement()

    if (selection !== null && nativeSelection !== null && rootElement !== null && rootElement.contains(nativeSelection.anchorNode) && editor.isEditable()) {
      const domRect: DOMRect | undefined = nativeSelection.focusNode?.parentElement?.getBoundingClientRect()
      if (domRect) {
        domRect.y += 40
        setFloatingElemPositionForLinkEditor(domRect, editorElem, anchorElem)
      }
      setLastSelection(selection)
    } else if (!activeElement || !activeElement.className.includes('rich-text-link-input')) {
      if (rootElement !== null) {
        setFloatingElemPositionForLinkEditor(null, editorElem, anchorElem)
      }
      setLastSelection(null)
      setIsLinkEditMode(false)
      setLinkUrl('')
    }

    return true
  }, [anchorElem, editor, setIsLinkEditMode, isLinkEditMode, linkUrl])

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement

    const update = () => {
      editor.getEditorState().read(() => {
        $updateLinkEditor()
      })
    }

    window.addEventListener('resize', update)

    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update)
    }

    return () => {
      window.removeEventListener('resize', update)

      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update)
      }
    }
  }, [anchorElem.parentElement, editor, $updateLinkEditor])

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateLinkEditor()
        })
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateLinkEditor()
          return true
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (isLink) {
            setIsLink(false)
            return true
          }
          return false
        },
        COMMAND_PRIORITY_HIGH,
      ),
    )
  }, [editor, $updateLinkEditor, setIsLink, isLink])

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateLinkEditor()
    })
  }, [editor, $updateLinkEditor])

  useEffect(() => {
    if (isLinkEditMode && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isLinkEditMode, isLink])

  const monitorInputInteraction = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleLinkSubmission()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setIsLinkEditMode(false)
    }
  }

  const handleLinkSubmission = () => {
    if (lastSelection !== null) {
      if (linkUrl !== '') {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
          url: sanitizeUrl(editedLinkUrl),
          target: '_blank',
          rel: 'noopener noreferrer',
        })
        editor.update(() => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            const parent = getSelectedNode(selection).getParent()
            if ($isAutoLinkNode(parent)) {
              const linkNode = $createLinkNode(parent.getURL(), {
                rel: parent.__rel,
                target: parent.__target,
                title: parent.__title,
              })
              parent.replace(linkNode, true)
            }
          }
        })
      }
      setEditedLinkUrl('https://')
      setIsLinkEditMode(false)
    }
  }

  return (
    <div ref={editorRef} className={classNames('d-flex position-absolute rich-text-link-editor-container', { 'd-none': !isLink })}>
      {!isLink ? null : isLinkEditMode ? (
        <div className='d-flex p-2 gap-2 align-items-center flex-grow-1'>
          <Form.Control
            ref={inputRef}
            className='p-1 rich-text-link-input'
            type='text'
            value={editedLinkUrl}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setEditedLinkUrl(event.target.value)
            }}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              monitorInputInteraction(event)
            }}
          />
          <div className='d-flex gap-2 align-items-center'>
            <Button
              variant='secondary'
              className='p-1'
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setIsLinkEditMode(false)
              }}
            >
              <i className='bi bi-x' />
            </Button>
            <Button variant='primary' className='p-1' onMouseDown={(event) => event.preventDefault()} onClick={handleLinkSubmission}>
              <i className='bi bi-check-lg' />
            </Button>
          </div>
        </div>
      ) : (
        <div className='d-flex flex-column p-2 gap-2 justify-content-center flex-grow-1'>
          <a
            className='paragraph-medium-extended text-break rich-text-link-editor-link text-decoration-underline'
            href={sanitizeUrl(linkUrl)}
            target='_blank'
            rel='noopener noreferrer'
          >
            {linkUrl}
          </a>
          <div className='d-flex gap-2 align-items-center align-self-end'>
            <Button
              variant='secondary'
              className='p-1'
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
              }}
            >
              <i className='bi bi-trash' />
            </Button>
            <Button
              variant='primary'
              className='p-1'
              onClick={() => {
                setEditedLinkUrl(linkUrl)
                setIsLinkEditMode(true)
              }}
            >
              <i className='bi bi-pencil' />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

const useFloatingLinkEditorToolbar = (
  editor: LexicalEditor,
  anchorElem: HTMLElement,
  isLink: boolean,
  setIsLink: Dispatch<boolean>,
  isLinkEditMode: boolean,
  setIsLinkEditMode: Dispatch<boolean>,
): JSX.Element | null => {
  useEffect(() => {
    // function $updateToolbar() {
    //   const selection = $getSelection()
    //   if ($isRangeSelection(selection)) {
    //     const focusNode = getSelectedNode(selection)
    //     const focusLinkNode = $findMatchingParent(focusNode, $isLinkNode)
    //     const focusAutoLinkNode = $findMatchingParent(focusNode, $isAutoLinkNode)
    //     if (!(focusLinkNode || focusAutoLinkNode)) {
    //       setIsLink(false)
    //       return
    //     }
    //     const badNode = selection
    //       .getNodes()
    //       .filter((node) => !$isLineBreakNode(node))
    //       .find((node) => {
    //         const linkNode = $findMatchingParent(node, $isLinkNode)
    //         const autoLinkNode = $findMatchingParent(node, $isAutoLinkNode)
    //         return (
    //           (focusLinkNode && !focusLinkNode.is(linkNode)) ||
    //           (linkNode && !linkNode.is(focusLinkNode)) ||
    //           (focusAutoLinkNode && !focusAutoLinkNode.is(autoLinkNode)) ||
    //           (autoLinkNode && !autoLinkNode.is(focusAutoLinkNode))
    //         )
    //       })
    //     if (!badNode) {
    //       setIsLink(true)
    //     } else {
    //       setIsLink(false)
    //     }
    //   }
    // }
    return mergeRegister(
      // editor.registerUpdateListener(({ editorState }) => {
      //   editorState.read(() => {
      //     $updateToolbar()
      //   })
      // }),
      // editor.registerCommand(
      //   SELECTION_CHANGE_COMMAND,
      //   (_payload, newEditor) => {
      //     $updateToolbar()
      //     setActiveEditor(newEditor)
      //     return false
      //   },
      //   COMMAND_PRIORITY_CRITICAL,
      // ),
      editor.registerCommand(
        CLICK_COMMAND,
        (payload) => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            const node = getSelectedNode(selection)
            const linkNode = $findMatchingParent(node, $isLinkNode)
            if ($isLinkNode(linkNode) && (payload.metaKey || payload.ctrlKey)) {
              window.open(linkNode.getURL(), '_blank')
              return true
            }
          }
          return false
        },
        COMMAND_PRIORITY_LOW,
      ),
    )
  }, [editor])

  return createPortal(
    <FloatingLinkEditor
      editor={editor}
      isLink={isLink}
      anchorElem={anchorElem}
      setIsLink={setIsLink}
      isLinkEditMode={isLinkEditMode}
      setIsLinkEditMode={setIsLinkEditMode}
    />,
    anchorElem,
  )
}

export interface FloatingLinkEditorPluginProps {
  anchorElem?: HTMLElement
  editor: LexicalEditor
  formatting: RichTextFormatting
}

export default function RichTextLinkEditor({ anchorElem = document.body, editor, formatting }: FloatingLinkEditorPluginProps): JSX.Element | null {
  const { isLink, setIsLink, isLinkEditMode, setIsLinkEditMode } = formatting

  return useFloatingLinkEditorToolbar(editor, anchorElem, isLink, setIsLink, isLinkEditMode, setIsLinkEditMode)
}
